import {Box, Tab, Tabs, CircularProgress, Typography, useMediaQuery} from "@mui/material";
import {
    Chart as ChartJS,
    CategoryScale,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from "chart.js";
import React, {useEffect, useRef, useState} from "react";
import {Line} from "react-chartjs-2";
import dayjs from "dayjs";
import json2mq from "json2mq";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const LineChartHook = ({chartData, selectedPortfolio, portfolioId, isLoggedIn, roiData, errorData}) => {
    const chartRef = useRef(null);
    const [filteredChartData, setFilteredChartData] = useState(null);
    const [timeRange, setTimeRange] = useState("1w");

    const [disabledTabs, setDisabledTabs] = useState({
        "1w": false,
        "1m": false,
        "3m": false,
        "6m": true,
        "1y": true,
    });
    const [isChartLoading, setIsChartLoading] = useState(true);


    useEffect(() => {
        if (chartData && portfolioId && chartData[portfolioId]?.dates?.length) {
            const dates = chartData[portfolioId].dates;
            const earliestDate = dayjs(dates[0]);
            const today = dayjs();

            setDisabledTabs({
                "1w": earliestDate.isAfter(today.subtract(7, "day")),
                "1m": earliestDate.isAfter(today.subtract(1, "month")),
                "3m": earliestDate.isAfter(today.subtract(3, "month")),
                "6m": earliestDate.isAfter(today.subtract(6, "month")),
                "1y": earliestDate.isAfter(today.subtract(1, "year")),
            });
        }
    }, [chartData, portfolioId]);

    useEffect(() => {
    if (chartData && portfolioId && chartData[portfolioId]) {
        setIsChartLoading(true); // Start loading

        setTimeRange("1w");
        filterDataByTimeRange("1w");

        // Optionally simulate delay (for UX consistency), or remove setTimeout if filtering is synchronous
        setTimeout(() => {
            setIsChartLoading(false); // Finish loading
        }, 300);
    }
}, [chartData, portfolioId]);


    const isMobile = window.innerWidth <= 768;

    const getChartOptions = (isMobile, minY) => ({
        scales: {
            x: {
                title: {
                    display: true,
                    text: "",
                    font: {size: isMobile ? 8 : 14, family: "Arial", weight: "bold"},
                    color: isLoggedIn ? "#4A4A4A" : "rgba(0,0,0,0.1)",
                },
                ticks: {
                    font: {size: isMobile ? 8 : 14, family: "Arial"},
                    color: true,
                    stepSize: undefined,
                    maxTicksLimit: Math.floor(isMobile ? 7 : 14),
                },
                grid: {display: false},
            },
            y: {
                position: "right",
                title: {
                    display: true,
                    text: "",
                    font: {size: isMobile ? 12 : 16, family: "Arial", weight: "bold"},
                    color: isLoggedIn ? "#4A4A4A" : "rgba(0,0,0,0.1)",
                },
                ticks: {
                    font: {size: isMobile ? 8 : 14, family: "Arial"},
                    color: isLoggedIn ? "#4A4A4A" : "rgba(0,0,0,0.1)",
                    callback: function (value) {
                        return value.toFixed(2) + "%";
                    },
                },
                grid: {display: true, color: "rgba(0, 0, 0, 0.05)"},
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: "top",
                labels: {
                    font: {family: "Arial", size: isMobile ? 8 : 12},
                    color: isLoggedIn ? "#4A4A4A" : "rgba(0,0,0,0.1)",
                },
            },
            title: {display: false},
            tooltip: isLoggedIn
                ? {
                      enabled: true,
                      callbacks: {
                          label: function (tooltipItem) {
                              return `Change: ${tooltipItem.raw.toFixed(2)}%`;
                          },
                      },
                      backgroundColor: "#ffffff",
                      titleFont: {family: "Arial", size: 14},
                      bodyFont: {family: "Arial", size: 12},
                      borderColor: "#333",
                      borderWidth: 1,
                      titleColor: "#4A4A4A",
                      bodyColor: "#4A4A4A",
                  }
                : false,
        },
        elements: {
            point: {radius: 0},
            line: {tension: 0.4, borderColor: "#7e7ef7", backgroundColor: "rgba(126, 126, 247, 0.3)"},
        },
        interaction: {mode: "nearest", axis: "x", intersect: false},
    });

    const filterDataByTimeRange = (range) => {
    setTimeRange(range);

    if (!chartData || !portfolioId || !chartData[portfolioId]) return;

    const portfolioData = chartData[portfolioId];
    const netWorthValues = portfolioData.net_worth_values || [];

    let sliceDays = 7;
    let roiKey = "seven_day_roi";

    if (range === "1m") {
        sliceDays = 30;
        roiKey = "thirty_day_roi";
    } else if (range === "3m") {
        sliceDays = 90;
        roiKey = "ninety_day_roi";
    } else if (range === "6m") {
        sliceDays = 180;
    } else if (range === "1y") {
        sliceDays = 365;
    }

    const today = new Date();
    const netWorthLength = netWorthValues.length;

    const notEnoughHistory = netWorthLength < sliceDays;

    if (notEnoughHistory) {
        // Not enough data history — use net worth from origin date
        const artificialDates = [];
        for (let i = netWorthLength - 1; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            artificialDates.push(
                d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
            );
        }

        const baseValue = netWorthValues[0] || 1;
        const percentChange = netWorthValues.map(v => ((v - baseValue) / baseValue) * 100);

        setFilteredChartData({
            dates: artificialDates,
            values: percentChange,
        });
        return;
    }

    // Enough data, try using ROI
    const roiValues = roiData?.[portfolioId]?.[roiKey] || [];
    const recentRoiValues = roiValues.slice(-sliceDays);

    const filledRoiValues = [];
    let lastValid = 0;
    for (let i = 0; i < recentRoiValues.length; i++) {
        const val = recentRoiValues[i];
        if (!isNaN(val)) {
            lastValid = val;
            filledRoiValues.push(val);
        } else {
            filledRoiValues.push(lastValid);
        }
    }

    const artificialDates = [];
    for (let i = sliceDays - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        artificialDates.push(
            d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
        );
    }

    const validCount = filledRoiValues.filter(v => typeof v === "number").length;

    if (validCount >= 5) {
        setFilteredChartData({
            dates: artificialDates,
            values: filledRoiValues,
        });
    } else {
        // Fallback to net worth
        const startIndex = netWorthLength - sliceDays;
        const netSlice = netWorthValues.slice(startIndex);
        const fallbackDates = [];

        for (let i = sliceDays - 1; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            fallbackDates.push(
                d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
            );
        }

        const baseValue = netSlice[0] || 1;
        const percentChange = netSlice.map(v => ((v - baseValue) / baseValue) * 100);

        setFilteredChartData({
            dates: fallbackDates,
            values: percentChange,
        });
    }
};



    const handleChange = (event, newValue) => {
        filterDataByTimeRange(newValue);
    };
    const mediaQueryVar = useMediaQuery(
        json2mq({
            minWidth: 900,
        })
    );
    return (
        <Box className={`${!isLoggedIn ? "opacity-50" : ""} line-graph`} sx={{position: "relative"}}>
            {/* Tabs */}
            <Box sx={{borderBottom: 1, borderColor: "divider", mb: "18px"}}>
                <Tabs value={timeRange} onChange={handleChange} aria-label="Time Range Tabs">
                    <Tab label="1W" value="1w" disabled={disabledTabs["1w"] || errorData} />
                    <Tab label="1M" value="1m" disabled={disabledTabs["1m"] || errorData} />
                    <Tab label="3M" value="3m" disabled={disabledTabs["3m"] || errorData} />
                    <Tab label="6M" value="6m" disabled={disabledTabs["6m"] || errorData} />
                    <Tab label="1Y" value="1y" disabled={disabledTabs["1y"] || errorData} />
                </Tabs>
            </Box>

            {isChartLoading ? (
    <Box sx={{ position: "absolute", top: "55%", left: "50%", transform: "translate(-50%, -50%)" }}>
        <CircularProgress />
    </Box>
) : errorData ? (
    <Box
        sx={{
            position: "absolute",
            top: "50%",
            left: mediaQueryVar ? "50%" : "0%",
            width: mediaQueryVar ? "auto" : "100%",
            transform: mediaQueryVar ? "translate(-50%, -50%)" : "translateY(-50%)",
            mt: 2,
            px: mediaQueryVar ? 0 : 2,
            textAlign: "center",
        }}
    >
        <Typography variant="h6" color="textSecondary">
            Check Chart data after a week
        </Typography>
    </Box>
) : (
                <Box
                    sx={{width: "100%", height: isMobile ? "400px" : "clamp(250px, 60vh, 600px)", position: "relative"}}
                >
                    <Line
                        ref={chartRef}
                        data={{
                            labels: filteredChartData?.dates || [],
                            datasets: [
                                {
                                    label: selectedPortfolio || "",
                                    data: filteredChartData?.values || [],
                                    fill: true,
                                },
                            ],
                        }}
                        options={getChartOptions(isMobile)}
                    />
                </Box>
            )}
        </Box>
    );
};

export default LineChartHook;
